import { useElplanBids, useElplanInfoTagsForDate } from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import type { ElplanInfoTagForDate } from 'api/elplan/elplan.types'
import type { RowValues, TableCellAdapter } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import Datetime from 'utils/datetime/datetime'
import { makeDayAheadBidTableAdapter } from './DayAheadBidTable.adapter'
import { getDayAheadBidTagDomainIssues } from './DayAheadBidTable.domain.helper'
import { buildDayAheadBidColumns, buildDayAheadBidRows, getDayAheadBidRowLabels } from './DayAheadBidTable.grid.helper'
import { seedDayAheadBidGrid } from './DayAheadBidTable.seeding.helper'
import type {
  DayAheadBidColumn,
  DayAheadBidProductionConsumption,
  DayAheadBidRow,
  DayAheadBidTagsStatus,
  SeededDayAheadBidGrid,
} from './DayAheadBidTable.types'

type UseDayAheadBidTableModelInput = {
  elplanInfoId: number
  startTime: ISODateTime
  endTime: ISODateTime
  partitionId: number | undefined
  productionConsumption: DayAheadBidProductionConsumption
  tagsForDate: ElplanInfoTagForDate[]
  tagsStatus: DayAheadBidTagsStatus
  requireTagForBlockBids: boolean
  blockBidInputCurrency: string
}

type DayAheadBidTableModel = {
  renderedColumns: DayAheadBidColumn[]
  configuredColumns: DayAheadBidColumn[]
  rows: DayAheadBidRow[]
  rowLabels: string[]
  adapter: TableCellAdapter
  valuesByRow: RowValues[]
  skipped: SeededDayAheadBidGrid['skipped']
  isBidsPending: boolean
  isBidsError: boolean
  tagsStatus: DayAheadBidTagsStatus
  configuredTagsStatus: DayAheadBidTagsStatus
  isReady: boolean
  saveContextKey: string
}

export function useDayAheadBidTableModel({
  elplanInfoId,
  startTime,
  endTime,
  partitionId,
  productionConsumption,
  tagsForDate,
  tagsStatus,
  requireTagForBlockBids,
  blockBidInputCurrency,
}: UseDayAheadBidTableModelInput): DayAheadBidTableModel {
  const date = Datetime.toLocalTime(startTime, 'onlyDate') as ISODateTime
  const hasExplicitPartition = partitionId !== undefined
  const saveContextKey = [
    elplanInfoId,
    date,
    startTime,
    endTime,
    partitionId ?? 'none',
    hasExplicitPartition ? 'aggregate+partition' : 'unavailable',
  ].join('|')
  const {
    data: configuredTagsForDate = [],
    isPending: isConfiguredTagsPending,
    isError: isConfiguredTagsError,
  } = useElplanInfoTagsForDate(elplanInfoId, date, undefined, requireTagForBlockBids && hasExplicitPartition)
  const {
    data: bids = [],
    isPending: isBidsPending,
    isError: isBidsError,
  } = useElplanBids(
    elplanInfoId,
    startTime,
    resolveElplanBidsQueryEndTime(endTime),
    undefined,
    partitionId,
    hasExplicitPartition
  )

  const configuredColumns = [
    ...buildDayAheadBidColumns(configuredTagsForDate, 'production'),
    ...buildDayAheadBidColumns(configuredTagsForDate, 'consumption'),
  ]
  const renderedColumns = [
    ...buildDayAheadBidColumns(tagsForDate, 'production'),
    ...buildDayAheadBidColumns(tagsForDate, 'consumption'),
  ].filter((column) =>
    tagsForDate.some((tag) => tag.id === column.tagId && tag.production_consumption === productionConsumption)
  )
  const rows = buildDayAheadBidRows(startTime, endTime)
  const rowLabels = getDayAheadBidRowLabels(rows, blockBidInputCurrency)
  const { valuesByRow, skipped } = seedDayAheadBidGrid({
    bids,
    rows,
    columns: configuredColumns,
    configuredTagsForDate,
  })
  const tagDomainIssues = getDayAheadBidTagDomainIssues(tagsForDate, configuredTagsForDate)
  const configuredTagsStatus = isConfiguredTagsError ? 'error' : isConfiguredTagsPending ? 'pending' : 'ready'
  const isReady =
    hasExplicitPartition &&
    requireTagForBlockBids &&
    tagsStatus === 'ready' &&
    configuredTagsStatus === 'ready' &&
    !isBidsPending &&
    !isBidsError &&
    tagDomainIssues.length === 0 &&
    skipped.length === 0
  const adapter = makeDayAheadBidTableAdapter(renderedColumns, rows)

  return {
    renderedColumns,
    configuredColumns,
    rows,
    rowLabels,
    adapter,
    valuesByRow,
    skipped,
    isBidsPending,
    isBidsError,
    tagsStatus,
    configuredTagsStatus,
    isReady,
    saveContextKey,
  }
}
